import { useState } from "react";
import { ChatTicket } from "@/services/chat-service";
import { Button } from "@/components/ui/button";
import { CheckCircle2, CalendarPlus, Loader2, Send } from "lucide-react";
import { formatPhone } from "@/utils/formatters";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { fetchSpecialtiesByClinic, DentalSpecialty } from "@/services/dental-specialty-service";
import { sendBookingInvite } from "@/services/public-scheduling-service";
import { fetchPatientsByPhone, PatientByPhone } from "@/services/patient-service";

interface ChatObserverTableProps {
  tickets: ChatTicket[];
  loading: boolean;
  onResolve: (ticket: ChatTicket) => void;
  onViewHistory: (ticket: ChatTicket) => void;
  selectedId?: string;
}

interface SchedulingModalState {
  open: boolean;
  ticket: ChatTicket | null;
}

export function ChatObserverTable({ tickets, loading, onResolve, onViewHistory, selectedId }: ChatObserverTableProps) {
  const { toast } = useToast();

  const [modal, setModal] = useState<SchedulingModalState>({ open: false, ticket: null });
  const [specialties, setSpecialties] = useState<DentalSpecialty[]>([]);
  const [specialtyId, setSpecialtyId] = useState('');
  const [doubleSlot, setDoubleSlot] = useState(false);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [patients, setPatients] = useState<PatientByPhone[]>([]);
  const [patientId, setPatientId] = useState('');

  // Cor determinística a partir do telefone — cada contato mantém a mesma cor
  const avatarColor = (seed: string) => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) hash = seed.charCodeAt(i) + ((hash << 5) - hash);
    const palette = [
      { bg: "bg-[hsl(158,28%,88%)]", border: "border-[hsl(158,34%,55%)]", text: "text-[hsl(158,40%,20%)]" },
      { bg: "bg-[hsl(32,45%,90%)]",  border: "border-[hsl(32,55%,58%)]",  text: "text-[hsl(28,50%,22%)]"  },
      { bg: "bg-[hsl(12,55%,92%)]",  border: "border-[hsl(12,60%,65%)]",  text: "text-[hsl(8,55%,28%)]"   },
      { bg: "bg-[hsl(220,30%,92%)]", border: "border-[hsl(220,35%,60%)]", text: "text-[hsl(220,40%,25%)]" },
      { bg: "bg-[hsl(280,22%,92%)]", border: "border-[hsl(280,28%,62%)]", text: "text-[hsl(280,35%,25%)]" },
      { bg: "bg-[hsl(180,25%,89%)]", border: "border-[hsl(180,32%,50%)]", text: "text-[hsl(180,42%,18%)]" },
    ];
    return palette[Math.abs(hash) % palette.length];
  };

  // Normaliza nome: "" / null / "Paciente Desconhecido" → "Desconhecido" (consistente
  // entre tickets criados por whapi/meta e twilio).
  const normalizeName = (name: string | null | undefined): string => {
    const trimmed = (name || "").trim();
    if (!trimmed || trimmed.toLowerCase() === "paciente desconhecido") {
      return "Desconhecido";
    }
    return trimmed;
  };

  const initials = (name: string | null | undefined, fallback: string) => {
    const src = (name || fallback || "?").trim();
    const parts = src.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "?";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const relativeTime = (iso: string) => {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "agora";
    if (mins < 60) return `${mins}min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d`;
    return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const handleOpenSchedulingModal = async (ticket: ChatTicket, e: React.MouseEvent) => {
    e.stopPropagation();
    setSpecialtyId('');
    setDoubleSlot(false);
    setPatientId('');
    setModal({ open: true, ticket });

    if (ticket.clinic_id) {
      setLoadingSpecialties(true);
      try {
        const [specialtiesData, patientsData] = await Promise.all([
          fetchSpecialtiesByClinic(ticket.clinic_id),
          fetchPatientsByPhone(ticket.cellphone, ticket.clinic_id),
        ]);
        setSpecialties(specialtiesData);
        setPatients(patientsData);
        if (patientsData.length === 1) setPatientId(patientsData[0].id);
      } catch {
        toast({ title: "Erro ao carregar dados", variant: "destructive" });
      } finally {
        setLoadingSpecialties(false);
      }
    }
  };

  const handleCloseModal = () => {
    setModal({ open: false, ticket: null });
    setSpecialties([]);
    setSpecialtyId('');
    setDoubleSlot(false);
    setPatients([]);
    setPatientId('');
  };

  const handleSendLink = async () => {
    if (!modal.ticket || !specialtyId || !modal.ticket.clinic_id) return;
    if (!patientId) {
      toast({
        title: "Selecione um paciente",
        description: "É necessário vincular o convite a um paciente cadastrado.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await sendBookingInvite({
        clinic_id: modal.ticket.clinic_id,
        cellphone: modal.ticket.cellphone,
        patient_id: patientId,
        specialty_id: specialtyId,
      });

      toast({
        title: "Convite enviado pelo WhatsApp! 📲",
        description: "O paciente receberá a mensagem em instantes.",
      });

      handleCloseModal();
    } catch (err: any) {
      toast({
        title: "Erro ao enviar convite",
        description: err.message || "Não foi possível enviar o convite de agendamento.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-card divide-y divide-border/60">
        {tickets.length === 0 && !loading ? (
          <div className="py-16 text-center">
            <p className="font-display italic text-lg text-muted-foreground">
              Caixa limpa. Aproveite o silêncio.
            </p>
          </div>
        ) : (
          tickets.map((ticket) => {
            const isPending = ticket.last_message_direction === 'IN';
            const isSelected = selectedId === ticket.id;
            const colors = avatarColor(ticket.cellphone || ticket.id);
            return (
              <div
                key={ticket.id}
                data-ticket-id={ticket.id}
                onClick={() => onViewHistory(ticket)}
                className={cn(
                  "group relative flex items-center gap-3 pl-4 pr-3 py-2.5 cursor-pointer transition-colors",
                  isSelected
                    ? "bg-primary/[0.08] hover:bg-primary/[0.1]"
                    : isPending
                      ? "bg-accent/[0.04] hover:bg-accent/[0.08]"
                      : "hover:bg-muted/40"
                )}
              >
                {/* Barra lateral — primary se selecionado, coral se pendente */}
                {(isSelected || isPending) && (
                  <span className={cn(
                    "absolute left-0 top-0 bottom-0 w-[3px]",
                    isSelected ? "bg-primary" : "bg-accent"
                  )} />
                )}

                {/* Avatar */}
                <div className={`flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full border ${colors.bg} ${colors.border} ${colors.text} font-display text-xs font-semibold relative`}>
                  {initials(ticket.patient_name, ticket.cellphone)}
                  {isPending && (
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-accent ring-2 ring-card">
                      <span className="absolute inset-0 rounded-full bg-accent animate-ping opacity-60" />
                    </span>
                  )}
                </div>

                {/* Conteúdo principal */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className={`text-sm truncate ${isPending ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
                      {normalizeName(ticket.patient_name)}
                    </span>
                    {/* Badge canal: Bot (twilio) vs Recepção (whapi/meta) */}
                    {ticket.provider === "twilio" ? (
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 shrink-0" title="Atendimento automatizado">
                        🤖 Bot
                      </span>
                    ) : (
                      <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 shrink-0" title="Atendimento humano da recepção">
                        👤 Recepção
                      </span>
                    )}
                    <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">
                      {formatPhone(ticket.cellphone)}
                    </span>
                    <span className="ml-auto text-[10px] text-muted-foreground font-display italic shrink-0 tabular-nums">
                      {relativeTime(ticket.last_message_at)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground italic truncate mt-0.5 pr-2">
                    {isPending && <span className="not-italic mr-1 text-accent">←</span>}
                    {(ticket.last_message_text || '').slice(0, 160) || <span className="opacity-60">(sem conteúdo)</span>}
                  </p>
                </div>

                {/* Ações — visíveis só no hover */}
                <div
                  className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-7 w-7"
                    onClick={(e) => handleOpenSchedulingModal(ticket, e)}
                    title="Convite de Agendamento"
                  >
                    <Send className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-[hsl(var(--success))] hover:bg-[hsl(var(--success)/0.12)] hover:text-[hsl(var(--success))]"
                    onClick={() => onResolve(ticket)}
                    title="Resolver / arquivar"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal de Agendamento */}
      <Dialog open={modal.open} onOpenChange={(open) => { if (!open) handleCloseModal(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Send className="h-5 w-5 text-primary" />
              Convite de Agendamento
            </DialogTitle>
          </DialogHeader>

          {modal.ticket && (
            <div className="space-y-4 py-2">
              <div className="bg-muted/60 border border-border rounded-md px-4 py-3 text-sm">
                <p className="font-semibold text-foreground">{modal.ticket.patient_name || "Paciente"}</p>
                <p className="text-muted-foreground tabular-nums">{formatPhone(modal.ticket.cellphone)}</p>
              </div>

              {/* Paciente */}
              <div className="space-y-2">
                <Label htmlFor="patient-select" className="text-sm font-semibold">
                  Paciente
                </Label>
                <select
                  id="patient-select"
                  value={patientId}
                  onChange={(e) => setPatientId(e.target.value)}
                  disabled={patients.length <= 1}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-60"
                >
                  {patients.length === 0 && <option value="">Nenhum paciente encontrado</option>}
                  {patients.length > 1 && <option value="">Selecione o paciente...</option>}
                  {patients.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Especialidade */}
              <div className="space-y-2">
                <Label htmlFor="specialty-select" className="text-sm font-semibold">
                  Especialidade
                </Label>
                {loadingSpecialties ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Carregando especialidades...
                  </div>
                ) : (
                  <select
                    id="specialty-select"
                    value={specialtyId}
                    onChange={(e) => setSpecialtyId(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Selecione uma especialidade...</option>
                    {specialties.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Duração */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Duração da Consulta</Label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setDoubleSlot(false)}
                    className={`flex-1 py-2 px-4 rounded-md border text-sm font-medium transition-all ${
                      !doubleSlot
                        ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                        : 'bg-card text-muted-foreground border-border hover:border-primary/50'
                    }`}
                  >
                    30 minutos
                  </button>
                  <button
                    type="button"
                    onClick={() => setDoubleSlot(true)}
                    className={`flex-1 py-2 px-4 rounded-md border text-sm font-medium transition-all ${
                      doubleSlot
                        ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                        : 'bg-card text-muted-foreground border-border hover:border-primary/50'
                    }`}
                  >
                    1 hora
                  </button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCloseModal} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              onClick={handleSendLink}
              disabled={!specialtyId || !patientId || submitting || loadingSpecialties}
              className="gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  Enviar Convite
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
